import { Ref, ref, shallowRef, computed } from '@vue/composition-api';
import type { SObject } from '@/types';
import { ValueField } from '@/services/SalesforceService.types';
import useDI from './useDI';

type SelectedState = Record<string, Record<string, true>>;

const LOCAL_STORAGE_KEY = 'SELECTED_CUSTOM_VALUE_FIELDS_STATE';

function getInitialSelectedState(): SelectedState {
  try {
    const serializedState = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (serializedState === null) {
      return {};
    }
    const state = JSON.parse(serializedState);
    return state;
  } catch (e) {
    console.error(e);
    return {};
  }
}

function saveToLocalStorage(ss: SelectedState) {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(ss));
  } catch (e) {
    console.error(e);
  }
}

const loading = ref(false);
const customValueFields = shallowRef([] as ValueField[]);
const selectedState = shallowRef(getInitialSelectedState());

function setSelectedState(value: SelectedState): void {
  selectedState.value = value;
  saveToLocalStorage(value);
}

export function getSelectedFieldsBySObject(sobject: SObject): string[] {
  return Object.keys(selectedState.value[sobject] ?? {});
}

export default function useCustomValueFields(sobject: Ref<SObject>) {
  const { container, Tokens } = useDI();
  const salesforceService = container.get(Tokens.SalesforceService);

  const selectedFieldsState = computed(() => {
    return selectedState.value[sobject.value] ?? {};
  });

  const selectedFields = computed(() => {
    return Object.keys(selectedState.value[sobject.value] ?? {});
  });

  const hasSelectedFields = computed(() => selectedFields.value.length !== 0);

  function setSelectedFieldsState(value: Record<string, true>) {
    setSelectedState({
      ...selectedState.value,
      [sobject.value]: value,
    });
  }

  function clearSelectedFieldsState() {
    const { [sobject.value]: _, ...rest } = selectedState.value;
    setSelectedState(rest);
  }

  const customFieldsByObject = computed(() => {
    const data: Record<string, ValueField[]> = {};
    for (const field of customValueFields.value) {
      if (!data[field.sobject]) {
        data[field.sobject] = [];
      }

      data[field.sobject].push(field);
    }

    return data;
  });

  async function load(): Promise<void> {
    loading.value = true;
    try {
      customValueFields.value = await salesforceService.getValueFields(
        sobject.value
      );

      // Remove fields that no longer exist
      const sobjectSelectedFields = selectedState.value[sobject.value] ?? {};
      const cleanSelectedFields: Record<string, true> = {};
      for (const field of customValueFields.value) {
        if (
          field.errors.length === 0 &&
          sobjectSelectedFields[field.fullyQualifiedName]
        ) {
          cleanSelectedFields[field.fullyQualifiedName] = true;
        }
      }

      if (
        Object.keys(sobjectSelectedFields).length !==
        Object.keys(cleanSelectedFields).length
      ) {
        // Only trigger reload when values are changed.
        setSelectedFieldsState(cleanSelectedFields);
      }
    } finally {
      loading.value = false;
    }
  }

  return {
    loading,
    customValueFields,
    customFieldsByObject,
    selectedFieldsState,
    selectedFields,
    hasSelectedFields,
    load,
    setSelectedFieldsState,
    clearSelectedFieldsState,
  };
}
